import React, { useEffect } from 'react';
import { Container, Row, Col, Badge, Card, CardBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import './policy.scss'

const PrivacyPolicyPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    // Table of contents
    const sections = [
        { id: 'introduction', title: '1. Introduction' },
        { id: 'data-collection', title: '2. Information We Collect' },
        { id: 'data-use', title: '3. How We Use Your Information' },
        { id: 'data-sharing', title: '4. Information Sharing & Disclosure' },
        { id: 'data-security', title: '5. Data Security' },
        { id: 'data-retention', title: '6. Data Retention' },
        { id: 'your-rights', title: '7. Your Rights' },
        { id: 'cookies', title: '8. Cookies & Tracking Technologies' },
        { id: 'international', title: '9. International Data Transfers' },
        { id: 'children', title: '10. Children\'s Privacy' },
        { id: 'changes', title: '11. Changes to This Policy' },
        { id: 'contact', title: '12. Contact Information' }
    ];

    return (
        <div className="pp-policy-page">
            {/* Hero Section */}
            <section className="pp-hero-section py-5 pt-7 bg-light">
                <Container>
                    <Row className="text-center">
                        <Col lg={8} className="mx-auto">

                            <h1 className="pp-hero-title display-4 fw-bold mb-4">
                                Our Commitment to
                                <span className="pp-gradient-text d-block">Your Privacy</span>
                            </h1>
                            <p className="pp-hero-subtitle lead text-muted mb-4">
                                This Privacy Policy explains how Reachtix collects, uses, discloses,
                                and safeguards your information when you use our platform and services.
                            </p>

                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Main Content */}
            <section className="pp-main-section py-5">
                <Container>
                    <Row>
                        {/* Sidebar Navigation */}
                        <Col lg={3} className="mb-5 mb-lg-0">
                            <Card className="pp-sidebar-card sticky-top" style={{ top: '100px' }}>
                                <CardBody className="p-4">
                                    <h6 className="pp-sidebar-title fw-bold mb-3">Table of Contents</h6>
                                    <nav className="pp-sidebar-nav">
                                        <ul className="list-unstyled mb-0">
                                            {sections.map((section) => (
                                                <li key={section.id} className="mb-2">
                                                    <a
                                                        href={`#${section.id}`}
                                                        className="pp-sidebar-link text-decoration-none d-block py-2 border-bottom border-light"
                                                    >
                                                        {section.title}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </nav>
                                    <div className="pp-sidebar-footer mt-4 pt-3 border-top">
                                        <small className="text-muted">
                                            Need help? <Link to="/contact" className="pp-contact-link text-primary">Contact us</Link>
                                        </small>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Main Content */}
                        <Col lg={9}>
                            <div className="pp-content-wrapper">
                                {/* Introduction */}
                                <section id="introduction" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">1. Introduction</h2>
                                    <p className="pp-section-text">
                                        Reachtix ("we," "our," or "us") is committed to protecting your privacy.
                                        This Privacy Policy applies to all users of our website, platform, and
                                        services (collectively, the "Services").
                                    </p>
                                    <p className="pp-section-text">
                                        We collect and process personal data in accordance with applicable data
                                        protection laws, including the General Data Protection Regulation (GDPR)
                                        and the California Consumer Privacy Act (CCPA).
                                    </p>
                                    <div className="pp-alert-info mt-4">
                                        <strong>Quick Summary:</strong> We only collect data necessary to provide
                                        our services and improve your experience. We never sell your personal
                                        information to third parties.
                                    </div>
                                </section>

                                {/* Information We Collect */}
                                <section id="data-collection" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">2. Information We Collect</h2>
                                    <p className="pp-section-text">We collect several types of information from and about users of our Services:</p>

                                    <h5 className="pp-subsection-title mt-4 mb-3">2.1 Personal Information You Provide</h5>
                                    <ul className="pp-section-list">
                                        <li><strong>Account Information:</strong> Name, email address, phone number, company details</li>
                                        <li><strong>Billing Information:</strong> Payment details, billing address, VAT number</li>
                                        <li><strong>Communication Data:</strong> Emails, support tickets, chat messages</li>
                                        <li><strong>Profile Information:</strong> Profile picture, preferences, settings</li>
                                    </ul>

                                    <h5 className="pp-subsection-title mt-4 mb-3">2.2 Information Collected Automatically</h5>
                                    <ul className="pp-section-list">
                                        <li><strong>Usage Data:</strong> Pages visited, features used, time spent</li>
                                        <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
                                        <li><strong>Location Data:</strong> Country, city (approximate location)</li>
                                        <li><strong>Cookies & Tracking:</strong> Session data, preferences, analytics</li>
                                    </ul>

                                    <h5 className="pp-subsection-title mt-4 mb-3">2.3 Information from Third Parties</h5>
                                    <ul className="pp-section-list">
                                        <li><strong>Social Media:</strong> When you connect social media accounts</li>
                                        <li><strong>Advertising Platforms:</strong> Google Ads, Facebook Ads API data</li>
                                        <li><strong>Analytics Providers:</strong> Google Analytics, Mixpanel</li>
                                        <li><strong>Payment Processors:</strong> Stripe, PayPal transaction data</li>
                                    </ul>
                                </section>

                                {/* How We Use Your Information */}
                                <section id="data-use" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">3. How We Use Your Information</h2>
                                    <p className="pp-section-text">We use the information we collect for the following purposes:</p>

                                    <Row className="mt-4">
                                        <Col md={6} className="mb-3">
                                            <div className="pp-purpose-card p-3 border rounded h-100">
                                                <h6 className="pp-purpose-title fw-bold">Service Delivery</h6>
                                                <p className="pp-purpose-text mb-0 small">To provide and maintain our Services, process payments, and manage your account.</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="pp-purpose-card p-3 border rounded h-100">
                                                <h6 className="pp-purpose-title fw-bold">Communication</h6>
                                                <p className="pp-purpose-text mb-0 small">To send service updates, security alerts, and support messages.</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="pp-purpose-card p-3 border rounded h-100">
                                                <h6 className="pp-purpose-title fw-bold">Improvement</h6>
                                                <p className="pp-purpose-text mb-0 small">To analyze usage patterns and improve our Services' functionality.</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="pp-purpose-card p-3 border rounded h-100">
                                                <h6 className="pp-purpose-title fw-bold">Marketing</h6>
                                                <p className="pp-purpose-text mb-0 small">To send promotional materials (with your consent) and measure campaign effectiveness.</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="pp-purpose-card p-3 border rounded h-100">
                                                <h6 className="pp-purpose-title fw-bold">Security</h6>
                                                <p className="pp-purpose-text mb-0 small">To detect and prevent fraud, abuse, and security issues.</p>
                                            </div>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <div className="pp-purpose-card p-3 border rounded h-100">
                                                <h6 className="pp-purpose-title fw-bold">Compliance</h6>
                                                <p className="pp-purpose-text mb-0 small">To comply with legal obligations and enforce our terms.</p>
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="mt-4">
                                        <h6 className="pp-subsection-title">Legal Basis for Processing (GDPR)</h6>
                                        <p className="pp-section-text">We process your personal data based on:</p>
                                        <ul className="pp-section-list">
                                            <li><strong>Contract:</strong> To fulfill our contractual obligations</li>
                                            <li><strong>Consent:</strong> When you've given explicit consent</li>
                                            <li><strong>Legitimate Interests:</strong> For business operations and improvements</li>
                                            <li><strong>Legal Obligations:</strong> To comply with applicable laws</li>
                                        </ul>
                                    </div>
                                </section>

                                {/* Information Sharing & Disclosure */}
                                <section id="data-sharing" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">4. Information Sharing & Disclosure</h2>
                                    <p className="pp-section-text">We may share your information in the following circumstances:</p>

                                    <h5 className="pp-subsection-title mt-4 mb-3">4.1 Service Providers</h5>
                                    <p className="pp-section-text">We engage trusted third-party service providers to assist with:</p>
                                    <ul className="pp-section-list">
                                        <li>Payment processing (Stripe, PayPal)</li>
                                        <li>Hosting and infrastructure (AWS, Google Cloud)</li>
                                        <li>Customer support (Intercom, Zendesk)</li>
                                        <li>Analytics and marketing (Google Analytics, HubSpot)</li>
                                        <li>Email communications (SendGrid, Mailchimp)</li>
                                    </ul>
                                    <p className="pp-note-text small text-muted">
                                        All service providers are contractually obligated to protect your data
                                        and use it only for the services they provide.
                                    </p>

                                    <h5 className="pp-subsection-title mt-4 mb-3">4.2 Legal Requirements</h5>
                                    <p className="pp-section-text">We may disclose your information if required by law or in response to:</p>
                                    <ul className="pp-section-list">
                                        <li>Court orders or legal processes</li>
                                        <li>Government requests</li>
                                        <li>To protect our rights, property, or safety</li>
                                        <li>To investigate fraud or security issues</li>
                                    </ul>

                                    <h5 className="pp-subsection-title mt-4 mb-3">4.3 Business Transfers</h5>
                                    <p className="pp-section-text">
                                        In the event of a merger, acquisition, or sale of assets, your
                                        information may be transferred as part of the transaction. We will
                                        notify you of any such change in ownership or control.
                                    </p>

                                    <h5 className="pp-subsection-title mt-4 mb-3">4.4 Aggregated Data</h5>
                                    <p className="pp-section-text">
                                        We may share aggregated, anonymized data that cannot be used to
                                        identify you for research, analytics, or marketing purposes.
                                    </p>
                                </section>

                                {/* Data Security */}
                                <section id="data-security" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">5. Data Security</h2>
                                    <p className="pp-section-text">
                                        We implement appropriate technical and organizational security measures
                                        to protect your personal data against unauthorized access, alteration,
                                        disclosure, or destruction.
                                    </p>

                                    <div className="pp-security-features mt-4">
                                        <h5 className="pp-subsection-title">Our Security Measures Include:</h5>
                                        <Row className="mt-3">
                                            <Col md={6} className="mb-3">
                                                <div className="pp-security-item d-flex align-items-start">
                                                    <span className="me-3">🔐</span>
                                                    <div>
                                                        <h6 className="pp-security-title fw-bold">Encryption</h6>
                                                        <p className="pp-security-text mb-0 small">Data encrypted in transit (SSL/TLS) and at rest</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="pp-security-item d-flex align-items-start">
                                                    <span className="me-3">🛡️</span>
                                                    <div>
                                                        <h6 className="pp-security-title fw-bold">Access Controls</h6>
                                                        <p className="pp-security-text mb-0 small">Role-based access and authentication requirements</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="pp-security-item d-flex align-items-start">
                                                    <span className="me-3">📋</span>
                                                    <div>
                                                        <h6 className="pp-security-title fw-bold">Regular Audits</h6>
                                                        <p className="pp-security-text mb-0 small">Security assessments and penetration testing</p>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <div className="pp-security-item d-flex align-items-start">
                                                    <span className="me-3">👨‍💼</span>
                                                    <div>
                                                        <h6 className="pp-security-title fw-bold">Employee Training</h6>
                                                        <p className="pp-security-text mb-0 small">Security awareness and data protection training</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="pp-alert-warning mt-4">
                                        <strong>Important:</strong> While we implement robust security measures,
                                        no method of transmission over the Internet or electronic storage is
                                        100% secure. We cannot guarantee absolute security.
                                    </div>
                                </section>

                                {/* Data Retention */}
                                <section id="data-retention" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">6. Data Retention</h2>
                                    <p className="pp-section-text">
                                        We retain your personal data only for as long as necessary to fulfill
                                        the purposes outlined in this policy, unless a longer retention period
                                        is required or permitted by law.
                                    </p>

                                    <div className="pp-retention-info mt-4">
                                        <h5 className="pp-subsection-title">Retention Periods:</h5>
                                        <ul className="pp-section-list">
                                            <li><strong>Account Data:</strong> Retained while your account is active, plus 30 days after deletion</li>
                                            <li><strong>Financial Records:</strong> 7 years for tax and accounting purposes</li>
                                            <li><strong>Support Communications:</strong> 3 years from last interaction</li>
                                            <li><strong>Analytics Data:</strong> 26 months from collection</li>
                                            <li><strong>Marketing Data:</strong> Until consent is withdrawn or 2 years of inactivity</li>
                                        </ul>
                                    </div>

                                    <p className="pp-section-text">
                                        When we no longer need to retain your data, we will securely delete or
                                        anonymize it in accordance with our data destruction policies.
                                    </p>
                                </section>

                                {/* Your Rights */}
                                <section id="your-rights" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">7. Your Rights</h2>
                                    <p className="pp-section-text">Depending on your location, you may have the following rights regarding your personal data:</p>

                                    <Row className="mt-4">
                                        <Col md={6} className="mb-3">
                                            <Card className="pp-rights-card h-100 border">
                                                <CardBody>
                                                    <h6 className="pp-rights-title fw-bold">Access & Portability</h6>
                                                    <p className="pp-rights-text small mb-0">Request access to your data and receive it in a structured format</p>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Card className="pp-rights-card h-100 border">
                                                <CardBody>
                                                    <h6 className="pp-rights-title fw-bold">Correction</h6>
                                                    <p className="pp-rights-text small mb-0">Request correction of inaccurate or incomplete data</p>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Card className="pp-rights-card h-100 border">
                                                <CardBody>
                                                    <h6 className="pp-rights-title fw-bold">Deletion</h6>
                                                    <p className="pp-rights-text small mb-0">Request deletion of your personal data ("right to be forgotten")</p>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Card className="pp-rights-card h-100 border">
                                                <CardBody>
                                                    <h6 className="pp-rights-title fw-bold">Objection & Restriction</h6>
                                                    <p className="pp-rights-text small mb-0">Object to processing or request restriction of processing</p>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Card className="pp-rights-card h-100 border">
                                                <CardBody>
                                                    <h6 className="pp-rights-title fw-bold">Withdraw Consent</h6>
                                                    <p className="pp-rights-text small mb-0">Withdraw consent at any time where processing is based on consent</p>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <Card className="pp-rights-card h-100 border">
                                                <CardBody>
                                                    <h6 className="pp-rights-title fw-bold">Opt-Out of Marketing</h6>
                                                    <p className="pp-rights-text small mb-0">Opt-out of marketing communications at any time</p>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <div className="mt-4">
                                        <h5 className="pp-subsection-title">Exercising Your Rights</h5>
                                        <p className="pp-section-text">
                                            To exercise any of these rights, please contact us at{' '}
                                            <a href="mailto:privacy@reachtix.com" className="pp-email-link">privacy@reachtix.com</a>.
                                            We will respond to your request within 30 days.
                                        </p>
                                        <p className="pp-note-text small text-muted">
                                            Note: We may need to verify your identity before processing certain requests.
                                        </p>
                                    </div>
                                </section>

                                {/* Cookies */}
                                <section id="cookies" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">8. Cookies & Tracking Technologies</h2>
                                    <p className="pp-section-text">
                                        We use cookies and similar tracking technologies to collect and store
                                        information when you use our Services.
                                    </p>

                                    <div className="mt-4">
                                        <h5 className="pp-subsection-title">Types of Cookies We Use:</h5>
                                        <table className="pp-cookies-table mt-3">
                                            <thead>
                                                <tr>
                                                    <th>Type</th>
                                                    <th>Purpose</th>
                                                    <th>Duration</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td><strong>Essential</strong></td>
                                                    <td>Required for basic site functionality</td>
                                                    <td>Session</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Preferences</strong></td>
                                                    <td>Remember your settings and preferences</td>
                                                    <td>1 year</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Analytics</strong></td>
                                                    <td>Understand how visitors use our site</td>
                                                    <td>2 years</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Marketing</strong></td>
                                                    <td>Deliver relevant advertisements</td>
                                                    <td>90 days</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4">
                                        <h5 className="pp-subsection-title">Managing Cookies</h5>
                                        <p className="pp-section-text">
                                            Most web browsers allow you to control cookies through their settings.
                                            You can usually find these settings in the "Options" or "Preferences"
                                            menu of your browser.
                                        </p>
                                        <p className="pp-section-text">
                                            <strong>Note:</strong> Disabling certain cookies may affect the
                                            functionality of our Services.
                                        </p>
                                    </div>
                                </section>

                                {/* International Transfers */}
                                <section id="international" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">9. International Data Transfers</h2>
                                    <p className="pp-section-text">
                                        Reachtix is operated from the United Kingdom. If you are located outside
                                        the UK, please be aware that your information may be transferred to,
                                        stored, and processed in the UK.
                                    </p>
                                    <p className="pp-section-text">
                                        We ensure that appropriate safeguards are in place for international
                                        data transfers, including:
                                    </p>
                                    <ul className="pp-section-list">
                                        <li>Standard Contractual Clauses (SCCs)</li>
                                        <li>Adequacy decisions where applicable</li>
                                        <li>Binding Corporate Rules for intra-group transfers</li>
                                    </ul>
                                </section>

                                {/* Children's Privacy */}
                                <section id="children" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">10. Children's Privacy</h2>
                                    <p className="pp-section-text">
                                        Our Services are not directed to individuals under the age of 16.
                                        We do not knowingly collect personal information from children under 16.
                                    </p>
                                    <p className="pp-section-text">
                                        If you become aware that a child under 16 has provided us with
                                        personal information, please contact us immediately. We will take
                                        steps to delete such information from our systems.
                                    </p>
                                </section>

                                {/* Changes to Policy */}
                                <section id="changes" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">11. Changes to This Policy</h2>
                                    <p className="pp-section-text">
                                        We may update this Privacy Policy from time to time to reflect changes
                                        in our practices, technologies, legal requirements, or other factors.
                                    </p>
                                    <p className="pp-section-text">
                                        When we make changes, we will update the "Last Updated" date at the
                                        top of this page. For material changes, we will provide additional
                                        notice, such as via email or a prominent notice on our website.
                                    </p>
                                    <p className="pp-section-text">
                                        We encourage you to review this Privacy Policy periodically to stay
                                        informed about how we protect your information.
                                    </p>
                                </section>

                                {/* Contact Information */}
                                <section id="contact" className="pp-content-section mb-5">
                                    <h2 className="pp-section-title fw-bold mb-4">12. Contact Information</h2>
                                    <p className="pp-section-text">If you have any questions or concerns about this Privacy Policy, please contact us:</p>

                                    <div className="pp-contact-info mt-4">
                                        <h5 className="pp-subsection-title">Reachtix Data Protection Officer</h5>
                                        <ul className="pp-contact-list list-unstyled">
                                            <li><strong>Email:</strong> <a href="mailto:privacy@reachtix.com" className="pp-email-link">privacy@reachtix.com</a></li>
                                            <li><strong>Postal Address:</strong> 330 High Holborn, London, WC1V 7QH, United Kingdom</li>
                                            <li><strong>Phone:</strong> +44 (0)20 XXXX XXXX</li>
                                        </ul>
                                    </div>

                                    <div className="mt-4">
                                        <h5 className="pp-subsection-title">Supervisory Authority</h5>
                                        <p className="pp-section-text">
                                            If you are located in the European Economic Area (EEA) and have
                                            concerns about our data practices, you have the right to lodge a
                                            complaint with your local data protection authority.
                                        </p>
                                    </div>
                                </section>

                                {/* Company Information */}
                                <div className="pp-company-card card bg-light border-0 mt-5">
                                    <div className="card-body">
                                        <h6 className="pp-company-title fw-bold">Company Information</h6>
                                        <p className="pp-company-text mb-2 small">
                                            <strong>Legal Name:</strong> QUANTUMCREST SOLUTIONS LTD (trading as Reachtix)
                                        </p>
                                        <p className="pp-company-text mb-2 small">
                                            <strong>Company Number:</strong> 16088299
                                        </p>
                                        <p className="pp-company-text mb-0 small">
                                            <strong>Registered Office:</strong> 330 High Holborn, London, WC1V 7QH, United Kingdom
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Quick Links Section */}
            <section className="pp-quicklinks-section py-5 bg-light">
                <Container>
                    <Row className="text-center">
                        <Col>
                            <h3 className="pp-quicklinks-title fw-bold mb-4">Related Documents</h3>
                            <div className="pp-quicklinks-container d-flex justify-content-center flex-wrap gap-3">
                                <Link to="/terms" className="pp-quicklink-btn btn btn-outline-primary">
                                    Terms of Service
                                </Link>
                                <Link to="/cookie-policy" className="pp-quicklink-btn btn btn-outline-primary">
                                    Cookie Policy
                                </Link>
                                <Link to="/data-processing" className="pp-quicklink-btn btn btn-outline-primary">
                                    Data Processing Agreement
                                </Link>
                                <Link to="/contact" className="pp-quicklink-btn btn btn-outline-primary">
                                    Contact Us
                                </Link>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default PrivacyPolicyPage;